import Box from "@mui/material/Box";
import { buttonClasses } from "@mui/material/Button";
import { formHelperTextClasses } from "@mui/material/FormHelperText";
import { inputLabelClasses } from "@mui/material/InputLabel";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import Paper from "@mui/material/Paper";
import { stackClasses } from "@mui/material/Stack";
import { alpha, getContrastRatio, styled } from "@mui/material/styles";

import { BET_COLUMN_WIDTH, GAME_SURFACE_HEIGHT, GAME_SURFACE_SHORT_SCREEN } from "@/constants";
import { colors } from "@/theme";

import {
  type FeltValue,
  type StageGlowOptions,
  type ToneValue,
  feltStyles,
  stageGlow,
  toneBlend,
  toneColor,
} from "./tokens";

/**
 * Ink on a chip.
 *
 * A chip keeps its color in both schemes, so what is written on it does not
 * move with the page either - it follows the chip: night ink on the cloud, sky
 * and crown chips, white on the deep ones.
 */
const CHIP_INK_DARK = "#04101f";
const CHIP_INK_LIGHT = "#ffffff";

const chipInk = (tone: string) =>
  getContrastRatio(tone, CHIP_INK_DARK) >= 4.5 ? CHIP_INK_DARK : CHIP_INK_LIGHT;

/** The mascot's own chip: cut from the night, edged in sky. */
const MASCOT_CHIP = { body: colors.layer1Dark, edge: "#5BC8FF" };

/**
 * The shell every in-house game is played in.
 *
 * One surface, two columns: what the player does on the left, what the game is
 * doing on the right. Keeping them joined means the stage never pushes the bet
 * button past the fold, which is the whole reason the controls moved out from
 * under it.
 *
 * A phone has no room for two columns, so they stack with the stage on top -
 * which is why the stage comes first in the markup and the controls column is
 * pulled left on desktop rather than the other way round.
 */
export const GameSurface = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: theme.shape.borderRadius,
  border: "1px solid",
  borderColor: theme.palette.divider,

  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    height: GAME_SURFACE_HEIGHT,
  },
  // Too short to divide: let the page scroll rather than clip the game.
  [GAME_SURFACE_SHORT_SCREEN]: {
    height: "auto",
  },
  // Unlike `hidden`, `clip` is no scroll container, so the phone action bar sticks to the page.
  "@supports (overflow: clip)": {
    [theme.breakpoints.down("md")]: {
      overflow: "clip",
    },
  },

  ...theme.applyStyles("dark", {
    backgroundColor: colors.layer1Dark,
  }),
}));

/** The stake, the controls, and the bets they produce. */
export const BetColumn = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  borderTop: "1px solid",
  borderColor: theme.palette.divider,

  [theme.breakpoints.up("md")]: {
    order: -1,
    width: BET_COLUMN_WIDTH,
    flexShrink: 0,
    borderTop: "none",
    borderRight: "1px solid",
    borderColor: theme.palette.divider,
  },
}));

/**
 * The controls themselves; they scroll when a game's options outgrow them.
 *
 * Labels sit above their field instead of floating into the outline. A
 * floating label is a nice trick on a form the player fills once; on a stake
 * field they retype every round it is a moving part reading the same word,
 * and the notch it cuts into the border costs width the column does not have.
 */
export const BetControlsRoot = styled(Box)(({ theme }) => ({
  // Natural height while the controls are short, and a scroller once they are
  // not - the auto tab is twice the height of the manual one, and a column
  // that cannot scroll puts the bet button somewhere nobody can press it.
  flexShrink: 1,
  minHeight: 0,
  overflowY: "auto",

  // The label is a flex child of the form control, so taking it out of the
  // absolute positioning MUI gives it drops it straight above the input.
  [`& .${inputLabelClasses.root}`]: {
    position: "static",
    transform: "none",
    maxWidth: "100%",
    marginBottom: theme.spacing(0.5),
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  // Nothing floats into the outline any more, so close the gap it cut.
  [`& .${outlinedInputClasses.notchedOutline} legend`]: {
    display: "none",
  },

  // Helper text sits under its field, not indented off the input's padding.
  [`& .${formHelperTextClasses.root}`]: {
    marginInline: 0,
  },

  // The stake is a figure the player edits every round: give it weight and fix
  // the digit widths so the field does not jitter as it is typed.
  [`& .${outlinedInputClasses.input}`]: {
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
  },

  // Drop the browser's number spinners. They are a light-UI control sitting in
  // a dark field, and the game has its own ways of changing a stake.
  '& input[type="number"]': {
    MozAppearance: "textfield",
  },
  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },

  // One thing to press, and it should look like it.
  [`& .${buttonClasses.sizeLarge}`]: {
    minHeight: 46,
    fontWeight: 700,
  },

  // The bet stays on screen while the rest of the controls scroll under it.
  // An autobet plan is tall enough to push the button out of the column, and
  // a button the player has to go looking for is a button that loses the round.
  //
  // A sticky element can only travel inside its own parent, so this pins the
  // action where it sits directly in the panel, and pins the row around it
  // where a game pairs it with a cash-out or a clear.
  [`& > .${stackClasses.root} > .${buttonClasses.containedSizeLarge}`]: {
    position: "sticky",
    bottom: 0,
    zIndex: 2,
  },
  [`& > .${stackClasses.root} .${stackClasses.root}:has(> .${buttonClasses.containedSizeLarge})`]: {
    position: "sticky",
    bottom: 0,
    zIndex: 2,
  },

  // Phones stack the stage above the controls: pin the action to the page, above the bottom nav.
  [theme.breakpoints.down("md")]: {
    overflowY: "visible",
    [`& > .${stackClasses.root} > .${buttonClasses.containedSizeLarge}`]: {
      bottom: "var(--game-action-bottom, 8px)",
      boxShadow: theme.shadows[6],
    },
    [`& > .${stackClasses.root} .${stackClasses.root}:has(> .${buttonClasses.containedSizeLarge})`]:
      {
        bottom: "var(--game-action-bottom, 8px)",
        paddingBlock: theme.spacing(1),
        backgroundColor: theme.vars.palette.background.paper,
      },
  },
}));

/**
 * What sits under the controls - the round's bets, the player's history, the
 * last result. It takes the rest of the column and scrolls inside it.
 */
export const BetsPane = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: theme.spacing(30),
  overflow: "hidden",

  [theme.breakpoints.up("md")]: {
    // Enough to stay a list rather than a sliver, and no more: the controls
    // above have first claim on the column.
    minHeight: theme.spacing(20),
  },
}));

/**
 * The game's own column: the stage and whatever reads above it.
 *
 * A few boards (the roulette wheel over its layout, the goal ladder) are
 * taller than a laptop screen at any sensible scale. They scroll inside this
 * column rather than being cropped by the surface, which keeps the controls
 * beside them fixed either way.
 */
export const StageColumn = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  overflowY: "auto",
}));

/** The game's name and the one control that belongs beside it. */
export const HeaderBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 2),
  borderBottom: "1px solid",
  borderColor: theme.palette.divider,
}));

/**
 * The stage a game is played on.
 *
 * Nineteen games had written this box themselves, and what they disagreed on
 * was never the game - it was the padding step and the opacity of the glow.
 * This owns the box model, the table and the light; a game says what color it
 * is lit with and keeps whatever its board genuinely needs (`minHeight` for the
 * tall ones) on the element.
 */
export const BoardStage = styled(Box, {
  shouldForwardProp: (prop) => prop !== "glow" && prop !== "felt" && prop !== "bleed",
})<{
  /** How the stage is lit. Omitted, it takes the brand blue from above. */
  glow?: StageGlowOptions;
  /** The game's own table, if it has one, per color scheme. */
  felt?: FeltValue;
  /**
   * The stage's child is a canvas that draws its own board - plinko's ramp,
   * limbo's climb - so it gets the whole stage with no padding to centre in.
   */
  bleed?: boolean;
}>(({ theme, glow, felt, bleed }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  // Takes the height the surface gives it and centres the game in it, rather
  // than sitting at the top of a column that is now a screen tall.
  flex: 1,
  minHeight: 0,
  // Sets the table and the ink together: a game with its own felt keeps white
  // markings in both schemes, one played on the page follows the scheme.
  ...feltStyles(theme, felt),
  backgroundImage: stageGlow(theme, glow),

  ...(bleed
    ? { overflow: "hidden" }
    : {
        alignItems: "center",
        gap: theme.spacing(2.5),
        padding: theme.spacing(4, 2, 3),

        [theme.breakpoints.down("md")]: {
          padding: theme.spacing(3, 1.5, 2),
        },
        [theme.breakpoints.down("sm")]: {
          padding: theme.spacing(2.5, 1, 1.5),
        },
      }),
}));

/**
 * The round's verdict, over the board that produced it.
 *
 * `tone` is the outcome - `"win"`, `"lose"`, `"push"` - or a game's own color
 * where the verdict is not one of those (baccarat names the winning side, not
 * the player's result). Left off, the banner is neutral, which is what slide
 * shows while a round it has no stake in plays out.
 */
export const ResultBanner = styled(Box, {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone?: ToneValue }>(({ theme, tone }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  alignSelf: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(0.75, 2.5),
  borderRadius: 999,
  border: "1px solid",
  borderColor: tone ? toneBlend(theme, tone, 0.7) : theme.vars.palette.game.hairline,
  backgroundColor: toneBlend(theme, tone, tone ? 0.14 : 0.08),
  color: theme.vars.palette.game.ink,

  animation: "game-banner 340ms cubic-bezier(0.2, 1.4, 0.4, 1)",
  "@keyframes game-banner": {
    "0%": { transform: "translateY(8px) scale(0.94)", opacity: 0 },
    "100%": { transform: "translateY(0) scale(1)", opacity: 1 },
  },
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
}));

/**
 * The height the verdict line keeps whether or not it has a verdict in it.
 *
 * A banner is one line of `body2` inside its own padding and hairline, which
 * is what this measures - and it is also enough for the hint or the clock a
 * game puts there while there is nothing to report yet.
 */
const RESULT_SLOT_HEIGHT = 34;

/**
 * The line on the stage a round's verdict lands in.
 *
 * A verdict only exists once the round is over, so a board that *grows* its
 * banner on settling moves everything above it - the keno card, the gem row,
 * the dice bar - a banner's height upwards at the moment the player is looking
 * at it, then drops it all back on the next bet. The games that show a hint in
 * the meantime only trade that jump for a smaller one: a line of caption is
 * not as tall as a banner.
 *
 * So the line is always there and always the same height, and whatever the
 * round has to say drops into it - the board stays where it was played.
 *
 * A game whose verdict is taller than one line of text (a pocket beside the
 * figure, a two-line hint) raises it with a `minHeight` of its own.
 */
export const ResultSlot = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  // Stretched rather than centred: the slot is as wide as the stage, so what
  // sits in it is centred on the board rather than on itself.
  alignSelf: "stretch",
  // The stage shares its height out between the board and this line; the board
  // gives way first, because a banner has nothing to shrink into.
  flexShrink: 0,
  minHeight: RESULT_SLOT_HEIGHT,
});

/**
 * One past round, in the strip above the board.
 *
 * Three readings of the same chip, because a history means different things
 * per game: `solid` is a result that *is* a color (a roulette number, a
 * baccarat winner), `soft` a payout worth naming, `muted` a round that paid
 * nothing. `round` is for the games whose history is a row of discs.
 */
export const ResultPip = styled(Box, {
  shouldForwardProp: (prop) => prop !== "tone" && prop !== "variant" && prop !== "round",
})<{
  /** The outcome's color. Ignored by `muted`, which has no outcome to show. */
  tone?: ToneValue;
  variant?: "solid" | "soft" | "muted";
  round?: boolean;
}>(({ theme, tone = "win", variant = "solid", round }) => ({
  flexShrink: 0,
  minWidth: round ? 24 : 26,
  height: round ? 24 : 26,
  paddingInline: round ? 0 : 6,
  borderRadius: round ? "50%" : 6,
  display: "grid",
  placeItems: "center",
  fontSize: round ? "0.68rem" : "0.72rem",
  fontWeight: round ? 900 : 800,
  whiteSpace: "nowrap",

  // A filled pip carries the ink that belongs on a bright fill, which is dark
  // on a dark page and white on a light one - the fills swap with the scheme.
  ...(variant === "solid" && {
    color: theme.vars.palette.game.onAccent,
    backgroundColor: toneColor(theme, tone),
    border: `1px solid ${theme.vars.palette.game.hairline}`,
  }),
  ...(variant === "soft" && {
    color: toneColor(theme, tone),
    backgroundColor: toneBlend(theme, tone, 0.16),
    border: `1px solid ${toneBlend(theme, tone, 0.5)}`,
  }),
  ...(variant === "muted" && {
    color: theme.vars.palette.game.inkFaint,
    backgroundColor: theme.vars.palette.game.track,
    border: `1px solid ${theme.vars.palette.game.hairline}`,
  }),

  // The strip is clickable - a pip opens the round it stands for.
  cursor: "pointer",
  transition: "transform 160ms ease",
  "&:hover": { transform: "translateY(-2px)" },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "&:hover": { transform: "none" },
  },
}));

/** Where a timed round is up to, in words, on the stage. */
export const PhaseLine = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(0.5, 2),
  borderRadius: 999,
  border: `1px solid ${theme.vars.palette.game.hairline}`,
  backgroundColor: theme.vars.palette.game.track,
  color: theme.vars.palette.game.ink,
}));

/**
 * The same thing as a clock, beside it.
 *
 * Linear, because it *is* a clock: an eased countdown would be a lie about how
 * much time is left.
 */
export const PhaseBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== "progress",
})<{ progress: number }>(({ theme, progress }) => ({
  position: "relative",
  width: 120,
  height: 4,
  borderRadius: 2,
  backgroundColor: theme.vars.palette.game.track,
  overflow: "hidden",

  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    width: `${Math.max(0, Math.min(100, progress * 100))}%`,
    backgroundColor: colors.prime,
    transition: "width 200ms linear",
  },
}));

/**
 * A denomination the player bets in, on the games that bet in chips.
 *
 * Dashed edge and all: it should read as a thing you pick up, not a button.
 * A chip keeps its own ink in both schemes: it is a physical object sitting on
 * the table, not a surface that follows the page.
 */
export const ChipButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected" && prop !== "tone",
})<{ selected: boolean; tone: string }>(({ theme, selected, tone }) => ({
  width: 46,
  height: 46,
  flexShrink: 0,
  borderRadius: "50%",
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
  userSelect: "none",
  fontWeight: 900,
  fontSize: "0.78rem",
  color: chipInk(tone),
  backgroundColor: tone,
  border: `3px dashed ${alpha(chipInk(tone), 0.45)}`,

  // Lifted and ringed rather than merely tinted: which chip is in hand decides
  // what the next click costs, so it has to be legible at a glance. The ring is
  // the page's ink, not the brand blue - half the tray is that blue already.
  outline: selected ? `2px solid ${theme.vars.palette.game.ink}` : "none",
  outlineOffset: 3,
  transform: selected ? "translateY(-3px)" : "none",
  boxShadow: selected
    ? `0 0 0 3px ${alpha(colors.prime, 0.3)}, 0 6px 14px ${alpha("#000000", 0.45)}`
    : `0 4px 10px ${alpha("#000000", 0.4)}`,
  transition: "transform 160ms ease, box-shadow 160ms ease, outline-color 160ms ease",

  "@media (prefers-reduced-motion: reduce)": { transform: "none", transition: "none" },
}));

/**
 * What the player has already put on a spot, sat on top of it.
 *
 * The drop animates `translate` and `scale` rather than `transform`, which
 * leaves `transform` free for the games that centre the stack on a cell.
 */
export const ChipStack = styled(Box, {
  shouldForwardProp: (prop) => prop !== "small",
})<{
  /** For a stack sat on a roulette cell, where a full-size chip would cover the number. */
  small?: boolean;
}>(({ small }) => ({
  position: "absolute",
  minWidth: small ? 18 : 24,
  height: small ? 18 : 24,
  paddingInline: small ? 3 : 5,
  borderRadius: 999,
  display: "grid",
  placeItems: "center",
  fontSize: small ? 8 : "0.66rem",
  fontWeight: 900,
  color: CHIP_INK_LIGHT,
  backgroundColor: MASCOT_CHIP.body,
  border: `2px dashed ${MASCOT_CHIP.edge}`,
  boxShadow: `0 2px 6px ${alpha("#000000", 0.5)}`,
  pointerEvents: "none",
  zIndex: 4,

  // Dropped, not faded in: the chip lands where the player put it.
  animation: "game-chip-drop 220ms cubic-bezier(0.2, 1.5, 0.4, 1)",
  "@keyframes game-chip-drop": {
    "0%": { translate: "0 -140%", scale: 0.7, opacity: 0 },
    "100%": { translate: "0 0", scale: 1, opacity: 1 },
  },
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
}));
